import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, RotateCcw, Check, AlertCircle, ArrowRight, X, ZoomIn, ZoomOut } from "lucide-react";
import { Modal } from "./Modal";

/**
 * IdScannerModal — guided assistant: capture → adjust → confirm.
 *
 * Steps per side: 2 sides × N persons (1 für Einzelperson, 2 für Eheleute).
 *
 * Innerhalb eines Steps:
 *   - mode "idle":   Aufnahmemethode wählen (Kamera-App oder Live-Vorschau)
 *   - mode "live":   getUserMedia-Stream mit ID-Rahmen-Overlay + Aufnahme
 *   - mode "adjust": Bild im ID-Rahmen verschieben & zoomen, dann bestätigen
 *   - mode "error":  Kamerafehler — Fallback zur Datei-Aufnahme
 *
 * Ausgabe: komprimiertes JPEG (max 1400 px Breite, Qualität 0.78), ID-1-Format
 * 1.586:1.
 */

const ID_RATIO = 85.6 / 53.98; // 1.586
const OUTPUT_WIDTH = 1400;
const OUTPUT_HEIGHT = Math.round(OUTPUT_WIDTH / ID_RATIO);
const JPEG_QUALITY = 0.78;

export const IdScannerModal = ({ open, onClose, persons, onCapture }) => {
  const steps = persons.flatMap((p) => [
    { personKey: p.key, label: p.label, side: "front", current: p.front },
    { personKey: p.key, label: p.label, side: "back", current: p.back },
  ]);
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (!open) return;
    const firstMissing = steps.findIndex((s) => !s.current);
    setStepIdx(firstMissing === -1 ? steps.length - 1 : firstMissing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  if (steps.length === 0) return null;

  const current = steps[stepIdx];
  const totalSteps = steps.length;
  const sideLabel = current.side === "front" ? "Vorderseite" : "Rückseite";

  const next = () => {
    if (stepIdx < steps.length - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      onClose?.();
    }
  };

  const retake = () => {
    onCapture(current.personKey, current.side, "");
  };

  return (
    <Modal
      open={open}
      title={`Ausweis aufnehmen — Schritt ${stepIdx + 1} von ${totalSteps}`}
      testIdPrefix="id-modal"
      onClose={onClose}
      maxWidthClass="max-w-2xl"
      footer={
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={retake}
              disabled={!current.current}
              data-testid="id-modal-retake"
              className="text-[12px] font-semibold inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[#e8e6df] text-[#505a74] disabled:opacity-40 hover:border-[#0a1128] hover:text-[#0a1128] transition"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Neu aufnehmen
            </button>
            <button
              type="button"
              onClick={next}
              disabled={!current.current}
              data-testid="id-modal-next"
              className="btn-primary px-5 py-2.5 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
            >
              {stepIdx < totalSteps - 1 ? (
                <>Weiter <ArrowRight className="w-4 h-4" /></>
              ) : (
                <>Fertig <Check className="w-4 h-4" /></>
              )}
            </button>
          </div>
          <div className="flex items-center justify-center gap-1.5">
            {steps.map((s, i) => (
              <span
                key={i}
                className={`h-2 rounded-full transition ${
                  i === stepIdx
                    ? "bg-[#d90429] w-5"
                    : s.current
                      ? "bg-[#0a1128] w-2"
                      : "bg-[#d4d2c8] w-2"
                }`}
              />
            ))}
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="bg-[#fff8e6] border border-[#f0d77a] rounded-sm px-4 py-3 text-[13px] text-[#5b4500]">
          <strong className="text-[#0a1128]">{current.label} — {sideLabel}</strong>
          <span className="block text-[12px] mt-0.5">
            Bitte den Ausweis gut ausgeleuchtet ablichten. Anschließend können
            Sie das Bild im Rahmen positionieren.
          </span>
        </div>

        <CaptureZone
          key={`${current.personKey}-${current.side}`}
          sideLabel={sideLabel}
          value={current.current}
          onCapture={(b64) => onCapture(current.personKey, current.side, b64)}
        />
      </div>
    </Modal>
  );
};

/* ============================================================== */
/*                          Capture Zone                          */
/* ============================================================== */

const CaptureZone = ({ sideLabel, value, onCapture }) => {
  // mode: idle | live | adjust | error
  const [mode, setMode] = useState("idle");
  const [error, setError] = useState("");
  const [rawSrc, setRawSrc] = useState(""); // unkomprimiertes Bild zur Positionierung

  // ---- live camera ----------------------------------------------------
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch (_) { /* noop */ }
    }
  }, []);

  // Wenn das Modal/Step gewechselt wird → Stream sauber stoppen
  useEffect(() => () => stopStream(), [stopStream]);

  // Sobald wir in den live-mode wechseln und ein Stream existiert, an das
  // (jetzt im DOM verfügbare) <video>-Element binden und play() aufrufen.
  useEffect(() => {
    if (mode !== "live" || !streamRef.current || !videoRef.current) return;
    const v = videoRef.current;
    v.srcObject = streamRef.current;
    const play = async () => {
      try {
        await v.play();
      } catch (e) {
        // Manche Browser werfen NotAllowedError trotz user gesture; Bild bleibt
        // sichtbar wenn srcObject gesetzt ist.
        console.warn("video.play() abgelehnt:", e);
      }
    };
    play();
  }, [mode]);

  const startCamera = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Ihr Browser unterstützt die Kamera-Funktion nicht. Bitte verwenden Sie einen aktuellen Browser (Safari, Chrome, Firefox).');
      setMode("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setMode("live");
    } catch (e) {
      console.error("Camera error", e);
      setError(
        "Kamerazugriff wurde verweigert oder ist nicht verfügbar. Bitte erlauben Sie den Zugriff in den Browser-Einstellungen und versuchen Sie es erneut.",
      );
      setMode("error");
    }
  };

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return;
    // Vollständigen Frame in eine PNG-DataURL ausziehen → adjust-Step
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const src = c.toDataURL("image/jpeg", 0.92);
    setRawSrc(src);
    stopStream();
    setMode("adjust");
  };

  // ---- bereits aufgenommenes Bild → Vorschau --------------------------
  if (value && mode !== "adjust") {
    return (
      <div className="relative bg-white border border-[#e8e6df] rounded-sm overflow-hidden">
        <img
          src={value}
          alt={`Ausweis ${sideLabel}`}
          className="block w-full h-auto"
          data-testid="id-modal-preview"
        />
        <div className="absolute top-2 right-2 bg-white/95 px-2.5 py-1 rounded-full text-[11px] font-semibold text-emerald-700 inline-flex items-center gap-1">
          <Check className="w-3.5 h-3.5" /> Aufgenommen
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {mode === "idle" && (
        <div className="bg-[#fafaf7] border border-dashed border-[#d4d2c8] rounded-sm p-6 text-center">
          <Camera className="w-8 h-8 text-[#505a74] mx-auto mb-3" />
          <p className="text-[13px] text-[#505a74] mb-4">
            Bitte den Ausweis vor die Kamera halten und mit einem Klick aufnehmen.
          </p>
          <button
            type="button"
            onClick={startCamera}
            data-testid="id-modal-live-start"
            className="btn-primary px-5 py-3 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2"
          >
            <Camera className="w-4 h-4" /> Kamera starten
          </button>
        </div>
      )}

      {mode === "live" && (
        <div className="relative bg-black rounded-sm overflow-hidden">
          <div className="relative" style={{ aspectRatio: "4 / 3", maxHeight: "45vh" }}>
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="id-modal-live-video"
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className="border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] relative"
                style={{ width: "86%", aspectRatio: "1.586 / 1" }}
              >
                <Corner pos="tl" />
                <Corner pos="tr" />
                <Corner pos="bl" />
                <Corner pos="br" />
              </div>
            </div>
            <div className="absolute top-3 left-0 right-0 text-center text-white/90 text-[11px] font-semibold tracking-wider uppercase pointer-events-none">
              Ausweis im Rahmen positionieren
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 p-3 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent">
            <button
              type="button"
              onClick={() => { stopStream(); setMode("idle"); }}
              className="text-white/85 text-[12px] inline-flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" /> Abbrechen
            </button>
            <button
              type="button"
              onClick={capture}
              data-testid="id-modal-live-capture"
              className="bg-white text-[#0a1128] px-5 py-2.5 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 shadow"
            >
              <Camera className="w-4 h-4" /> Aufnehmen
            </button>
          </div>
        </div>
      )}

      {mode === "adjust" && rawSrc && (
        <AdjustStage
          src={rawSrc}
          onCancel={() => {
            setRawSrc("");
            setMode("idle");
          }}
          onConfirm={(b64) => {
            onCapture(b64);
            setRawSrc("");
            setMode("idle");
          }}
        />
      )}

      {mode === "error" && (
        <div className="space-y-3">
          <div className="bg-[#fef2f2] border border-[#fecaca] rounded-sm p-4 text-[13px] text-[#991b1b] flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
          <button
            type="button"
            onClick={startCamera}
            data-testid="id-modal-retry"
            className="btn-primary px-5 py-3 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <Camera className="w-4 h-4" /> Erneut versuchen
          </button>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

const Corner = ({ pos }) => {
  const cls = {
    tl: "-top-1 -left-1 border-t-2 border-l-2",
    tr: "-top-1 -right-1 border-t-2 border-r-2",
    bl: "-bottom-1 -left-1 border-b-2 border-l-2",
    br: "-bottom-1 -right-1 border-b-2 border-r-2",
  }[pos];
  return <div className={`absolute w-4 h-4 border-[#d90429] ${cls}`} />;
};

/* ============================================================== */
/*               Adjust Stage — verschieben & zoomen              */
/* ============================================================== */

const AdjustStage = ({ src, onCancel, onConfirm }) => {
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });

  // Stage-Maße ermitteln
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setStageSize({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Bild geladen → natürliche Größe + initialer scale, der die Bühne füllt
  const onImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    setNatural({ w: nw, h: nh });
    if (stageSize.w && stageSize.h) {
      const s = Math.max(stageSize.w / nw, stageSize.h / nh);
      setMinScale(s);
      setScale(s);
      setOffset({ x: 0, y: 0 });
    }
  };

  // wenn stageSize sich ändert nachdem Bild bereits geladen
  useEffect(() => {
    if (!natural.w || !stageSize.w) return;
    const s = Math.max(stageSize.w / natural.w, stageSize.h / natural.h);
    setMinScale(s);
    setScale((prev) => (prev < s ? s : prev));
  }, [stageSize, natural]);

  const clampOffset = (next, sc) => {
    const w = natural.w * sc;
    const h = natural.h * sc;
    const maxX = Math.max(0, (w - stageSize.w) / 2);
    const maxY = Math.max(0, (h - stageSize.h) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, next.x)),
      y: Math.min(maxY, Math.max(-maxY, next.y)),
    };
  };

  // Drag-Handler (Maus + Touch)
  const onPointerDown = (e) => {
    e.preventDefault();
    const p = "touches" in e ? e.touches[0] : e;
    dragRef.current = {
      active: true,
      startX: p.clientX,
      startY: p.clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const p = "touches" in e ? e.touches[0] : e;
    const dx = p.clientX - dragRef.current.startX;
    const dy = p.clientY - dragRef.current.startY;
    setOffset(clampOffset({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy }, scale));
  };
  const onPointerUp = () => { dragRef.current.active = false; };

  const onScaleChange = (e) => {
    const s = parseFloat(e.target.value);
    setScale(s);
    setOffset((o) => clampOffset(o, s));
  };

  const confirm = () => {
    // In Output-Canvas zeichnen
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_WIDTH;
    canvas.height = OUTPUT_HEIGHT;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Bühne ist sichtbarer Bereich; wir rendern den Bühnen-Inhalt in Output skaliert
    const k = OUTPUT_WIDTH / stageSize.w;
    const drawW = natural.w * scale * k;
    const drawH = natural.h * scale * k;
    const cx = OUTPUT_WIDTH / 2 + offset.x * k;
    const cy = OUTPUT_HEIGHT / 2 + offset.y * k;
    ctx.drawImage(imgRef.current, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
    onConfirm(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
  };

  return (
    <div className="space-y-3">
      <div className="bg-[#0a1128] text-white/80 text-[12px] px-3 py-2 rounded-sm flex items-center gap-2">
        <Camera className="w-3.5 h-3.5" />
        <span>Ausweis im Rahmen ausrichten — verschieben &amp; zoomen.</span>
      </div>
      <div
        ref={stageRef}
        className="relative bg-black rounded-sm overflow-hidden select-none touch-none"
        style={{ aspectRatio: "1.586 / 1", maxHeight: "45vh" }}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchStart={onPointerDown}
        onTouchMove={onPointerMove}
        onTouchEnd={onPointerUp}
        data-testid="id-modal-adjust-stage"
      >
        <img
          ref={imgRef}
          src={src}
          onLoad={onImgLoad}
          draggable={false}
          alt="Aufgenommenes Ausweisdokument zum Ausrichten"
          className="absolute top-1/2 left-1/2 max-w-none pointer-events-none"
          style={{
            transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transformOrigin: "center center",
            width: natural.w || "auto",
            height: natural.h || "auto",
          }}
        />
        {/* Frame Hinweis */}
        <div className="absolute inset-2 border-2 border-white/80 pointer-events-none">
          <Corner pos="tl" />
          <Corner pos="tr" />
          <Corner pos="bl" />
          <Corner pos="br" />
        </div>
      </div>

      <div className="flex items-center gap-3 px-1">
        <ZoomOut className="w-4 h-4 text-[#505a74] shrink-0" />
        <input
          type="range"
          min={minScale}
          max={minScale * 4}
          step={0.01}
          value={scale}
          onChange={onScaleChange}
          data-testid="id-modal-adjust-zoom"
          className="flex-1 accent-[#d90429]"
        />
        <ZoomIn className="w-4 h-4 text-[#505a74] shrink-0" />
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onCancel}
          data-testid="id-modal-adjust-cancel"
          className="text-[12px] font-semibold inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[#e8e6df] text-[#505a74] hover:border-[#0a1128] hover:text-[#0a1128] transition"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Neu aufnehmen
        </button>
        <button
          type="button"
          onClick={confirm}
          data-testid="id-modal-adjust-confirm"
          className="btn-primary px-5 py-2.5 rounded-full text-[13px] font-semibold inline-flex items-center gap-2"
        >
          <Check className="w-4 h-4" /> Bild übernehmen
        </button>
      </div>
    </div>
  );
};
